/**
 * DS_20 — homepage first-fold hero (all widths; phone extras <= 767px).
 * Theme-native port of the won Intelligems variant. Markup is server-rendered
 * by sections/ds20-home-hero.liquid; this file only adds behavior:
 *   1. Phones: category cards become a Flickity carousel (window.Flickity,
 *      bundled by the theme). Without it the cards simply stay stacked.
 *   2. Phones: the rating link opens a reviews bottom sheet built from the
 *      reviews page (the link's own href). Until that page has been fetched —
 *      or if the fetch fails — and on wider screens, the link just opens it.
 *   3. GA4 interaction events for the hero's own elements (dataLayer, same
 *      event shape as the experiment). Dropped from the won script: the
 *      Intelligems goals, the Clarity "AB-Test" tag, the "Test / DS_20_Var"
 *      event and the listeners on OTHER homepage sections — those labelled
 *      sessions as an experiment variant and mean nothing once it is live.
 */

const MOBILE_MAX_WIDTH = 767;
const SHEET_OPEN_CLASS = 'js-ds20-sheet-open';
const FLICKITY_POLL_INTERVAL = 100;
const FLICKITY_POLL_TIMEOUT = 10000;
const GA_CATEGORY = 'DS_20';

// Pieces of the reviews page the sheet shows, in order.
const REVIEW_PARTS = [
  '.section-hero-trust-banner',
  '.reeview-app-widget',
  '.jdgm-all-reviews__summary',
  '.jdgm-histogram',
  '.jdgm-all-reviews__body',
];

const track = (action, label = '') => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'Intelligems_Test_Tracking',
    category: GA_CATEGORY,
    action,
    label,
  });
};

const waitForFlickity = () =>
  new Promise((resolve, reject) => {
    const startedAt = Date.now();
    const timer = setInterval(() => {
      if (window.Flickity) {
        clearInterval(timer);
        resolve(window.Flickity);
      } else if (Date.now() - startedAt > FLICKITY_POLL_TIMEOUT) {
        clearInterval(timer);
        reject(new Error('window.Flickity never loaded'));
      }
    }, FLICKITY_POLL_INTERVAL);
  });

const initCarousel = async (root) => {
  const cards = root.querySelector('[data-ds20-cards]');

  if (!cards) {
    return;
  }

  try {
    const Flickity = await waitForFlickity();
    const flkty = new Flickity(cards, {
      cellSelector: '.ds20__card',
      cellAlign: 'left',
      contain: true,
      wrapAround: false,
      prevNextButtons: true,
      pageDots: true,
      draggable: true,
    });
    let lastIndex = flkty.selectedIndex;

    flkty.on('change', (index) => {
      track('Homepage- Collections Interaction', `swipe ${index > lastIndex ? 'right' : 'left'}`);
      lastIndex = index;
    });
  } catch (error) {
    console.error('[DS_20] category carousel not initialised:', error);
  }
};

const fetchReviewParts = async (url) => {
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`reviews page returned ${response.status}`);
  }

  const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
  const parts = REVIEW_PARTS.map((selector) => doc.querySelector(selector)).filter(Boolean);
  const header = doc.querySelector('.jdgm-all-reviews__header[data-average-rating]');

  return { parts, avgRating: header ? header.getAttribute('data-average-rating') : null };
};

const FOCUSABLE = 'a[href], button:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])';

// Prefetch once the page is idle so the first tap opens instantly, without
// competing with the first-fold load.
const whenIdle = () =>
  new Promise((resolve) => {
    const schedule = () =>
      'requestIdleCallback' in window ? window.requestIdleCallback(resolve, { timeout: 4000 }) : setTimeout(resolve, 2000);

    if (document.readyState === 'complete') {
      schedule();
    } else {
      window.addEventListener('load', schedule, { once: true });
    }
  });

const initReviewsSheet = async (root) => {
  const link = root.querySelector('[data-ds20-reviews-open]');
  const sheet = root.querySelector('[data-ds20-sheet]');
  const content = root.querySelector('[data-ds20-sheet-content]');

  if (!link || !sheet || !content || !link.href) {
    return;
  }

  let parts;
  let avgRating;

  await whenIdle();

  try {
    ({ parts, avgRating } = await fetchReviewParts(link.href));
  } catch (error) {
    console.error('[DS_20] reviews sheet unavailable, rating link will navigate instead:', error);
    return;
  }

  if (parts.length === 0) {
    console.error('[DS_20] reviews page had none of the expected review blocks; rating link will navigate instead');
    return;
  }

  for (const part of parts) {
    content.appendChild(document.adoptNode(part));
  }

  const summaryStars = content.querySelector('.jdgm-all-reviews__summary-stars');

  if (summaryStars && avgRating) {
    const fromText = document.createElement('span');
    fromText.className = 'ds20-sheet__from';
    fromText.textContent = `from ${avgRating} out of 5`;
    summaryStars.appendChild(fromText);
  }

  const panel = sheet.querySelector('.ds20-sheet__panel');
  const closeButton = sheet.querySelector('.ds20-sheet__close-btn');

  // Modal dialog: focus moves in on open, Tab cycles inside the panel, and
  // focus returns to the rating link on close.
  const open = () => {
    sheet.hidden = false;
    document.documentElement.classList.add(SHEET_OPEN_CLASS);
    closeButton.focus();
  };
  const close = () => {
    document.documentElement.classList.remove(SHEET_OPEN_CLASS);
    sheet.hidden = true;
    link.focus();
  };

  link.addEventListener('click', (event) => {
    event.preventDefault();
    open();
  });

  for (const closer of sheet.querySelectorAll('[data-ds20-sheet-close]')) {
    closer.addEventListener('click', () => {
      if (closer.classList.contains('ds20-sheet__close-btn')) {
        track('BottomSheet CLOSE Interaction');
      }
      close();
    });
  }

  document.addEventListener('keydown', (event) => {
    if (sheet.hidden) {
      return;
    }

    if (event.key === 'Escape') {
      close();
      return;
    }

    if (event.key !== 'Tab') {
      return;
    }

    const focusable = [...panel.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (!panel.contains(document.activeElement)) {
      event.preventDefault();
      first.focus();
    } else if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });
};

const initTracking = (root) => {
  root.addEventListener('click', ({ target }) => {
    const cta = target.closest('.ds20__cta');

    if (cta) {
      track('Homepage Banner/Shop now interaction', cta.textContent.trim());
      return;
    }

    if (target.closest('.ds20__banner')) {
      track('Homepage Banner/Shop now interaction', 'Banner');
      return;
    }

    if (target.closest('.ds20__rating-link')) {
      track('Hyperlink Interaction on Banner');
      return;
    }

    if (target.closest('.reeview-app-widget')) {
      track('BottomSheet VIDEO Interaction');
      return;
    }

    const card = target.closest('.ds20__card');

    if (!card) {
      return;
    }

    if (target.closest('.ds20__card-cta')) {
      const title = card.querySelector('.ds20__card-title');
      track('Homepage- Collections Interaction', `Shop CTA - ${title ? title.textContent.trim() : ''}`);
    } else if (target.closest('.ds20__card-image')) {
      track('Homepage- Collections Interaction', 'Product Image');
    } else if (target.closest('.ds20__card-title')) {
      track('Homepage- Collections Interaction', 'Product Name');
    }
  });
};

const init = () => {
  const isPhone = document.documentElement.clientWidth <= MOBILE_MAX_WIDTH;

  for (const root of document.querySelectorAll('[data-ds20]')) {
    initTracking(root);

    if (isPhone) {
      initCarousel(root);
      initReviewsSheet(root);
    }
  }
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
